import { ErrorState } from '@/components/primitives/ErrorState'
import { btnSecondary } from '@/components/record-sheet/styles'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import {
  type WorkflowSummary,
  buildWorkflow,
  createWorkflow,
  fetchWorkflows
} from '@/lib/workflows'
import { useQuery } from '@tanstack/react-query'
import { ArrowUp, Loader2, Plus, Workflow as WorkflowIcon } from 'lucide-react'
import { type KeyboardEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AgentAvatar, ChiefOrb } from '../AgentAvatar'
import { RUN_TONE, timeAgo } from './shared'
import { TemplateGallery } from './TemplateGallery'

const IDEAS = [
  'When a chargeback comes in, have Billing gather the evidence, ask me before answering, then tell me the result.',
  'Every Monday at 9:00, have Content draft a weekly newsletter from last week’s best posts and save it for my review.',
  'When a new business is listed, have Listings check its details, then Merchant Success send a welcome email.',
  'When a 1★ or 2★ review is posted, have Trust & Safety check it and tell me if it breaks the rules.'
]

export function WorkflowsPage() {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState('')
  const [building, setBuilding] = useState(false)
  const [creating, setCreating] = useState(false)
  const list = useQuery({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    refetchInterval: (q) =>
      q.state.data?.workflows.some((w) => w.build_status === 'building') ? 3000 : 15_000
  })

  async function build() {
    const text = prompt.trim()
    if (!text || building) return
    setBuilding(true)
    try {
      const wf = await buildWorkflow(text)
      queryClient.setQueryData(['workflow', wf.id], wf)
      await queryClient.invalidateQueries({ queryKey: ['workflows'] })
      navigate(`/apps/ai-team/workflows/${wf.id}`)
    } catch (err) {
      notifyError('Could not start building', err)
    } finally {
      setBuilding(false)
    }
  }

  async function blank() {
    setCreating(true)
    try {
      const wf = await createWorkflow()
      await queryClient.invalidateQueries({ queryKey: ['workflows'] })
      navigate(`/apps/ai-team/workflows/${wf.id}`)
    } catch (err) {
      notifyError('Could not create it', err)
    } finally {
      setCreating(false)
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      build()
    }
  }

  const workflows = list.data?.workflows ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <section className="rounded-[36px] border border-neutral-200 bg-white p-6 sm:p-8 dark:border-neutral-800 dark:bg-black">
        <div className="flex items-center gap-4">
          <ChiefOrb size="lg" state={building ? 'thinking' : 'idle'} />
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-white">
              What should the team automate?
            </h2>
            <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
              Describe it in your words. Chief of Staff builds the workflow on a canvas while you watch.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-[36px] border border-neutral-200 bg-neutral-50 p-2 pl-5 transition-colors focus-within:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:focus-within:border-neutral-600">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKey}
            rows={3}
            maxLength={4000}
            placeholder="e.g. When a chargeback comes in, have Billing gather the evidence and ask me before answering it."
            aria-label="Describe the workflow"
            className="w-full resize-none bg-transparent py-2 text-[15px] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none dark:text-white"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] text-neutral-400">⌘ + Enter to build</span>
            <button
              type="button"
              onClick={build}
              disabled={!prompt.trim() || building}
              className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-40 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
            >
              {building ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
              Build it
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {IDEAS.map((idea) => (
            <button
              key={idea}
              type="button"
              onClick={() => setPrompt(idea)}
              className="max-w-full truncate rounded-full border border-neutral-200 px-3 py-1.5 text-left text-xs text-neutral-600 transition-colors hover:border-neutral-400 hover:text-neutral-900 dark:border-neutral-800 dark:text-neutral-300 dark:hover:border-neutral-600 dark:hover:text-white"
            >
              {idea}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="px-1">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Ready-made workflows</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Plug and play: pick one, answer a question or two, and it's ready to switch on.
          </p>
        </div>
        <TemplateGallery onAdded={(wf) => navigate(`/apps/ai-team/workflows/${wf.id}`)} />
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 px-1">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white">
            Your workflows
            {workflows.length > 0 && (
              <span className="ml-2 text-sm font-semibold tabular-nums text-neutral-400">
                {workflows.length}
              </span>
            )}
          </h3>
          <button type="button" className={btnSecondary} onClick={blank} disabled={creating}>
            {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            Start from scratch
          </button>
        </div>

        {list.error && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

        {list.isLoading && (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
            ))}
          </div>
        )}

        {list.data && workflows.length === 0 && (
          <div className="rounded-[36px] border border-dashed border-neutral-300 p-10 text-center dark:border-neutral-700">
            <WorkflowIcon className="mx-auto size-8 text-neutral-400" />
            <p className="mt-3 font-semibold text-neutral-900 dark:text-white">No workflows yet</p>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Describe one above, or pick an idea to start from.
            </p>
          </div>
        )}

        {workflows.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {workflows.map((w) => (
              <WorkflowCard key={w.id} wf={w} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function WorkflowCard({ wf }: { wf: WorkflowSummary }) {
  const building = wf.build_status === 'building'
  return (
    <Link
      to={`/apps/ai-team/workflows/${wf.id}`}
      className="group flex flex-col gap-3 rounded-[36px] border border-neutral-200 bg-white p-5 transition-colors hover:border-neutral-400 dark:border-neutral-800 dark:bg-black dark:hover:border-neutral-600"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-neutral-900 dark:text-white">{wf.name}</p>
          <p className="mt-0.5 line-clamp-2 text-sm text-neutral-500 dark:text-neutral-400">
            {wf.description || wf.trigger}
          </p>
        </div>
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
            building
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
              : wf.build_status === 'failed'
                ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                : wf.enabled
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                  : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
          )}
        >
          {building && <Loader2 className="size-3 animate-spin" />}
          {building ? 'Building' : wf.build_status === 'failed' ? 'Build failed' : wf.enabled ? 'On' : 'Off'}
        </span>
      </div>

      <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{wf.trigger}</p>

      <div className="mt-auto flex items-center gap-3">
        <div className="flex -space-x-1.5">
          {wf.agents.slice(0, 5).map((key) => (
            <AgentAvatar
              key={key}
              agentKey={key}
              size="sm"
              state={building ? 'working' : 'idle'}
              className="ring-2 ring-white dark:ring-black"
            />
          ))}
        </div>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {wf.steps} step{wf.steps === 1 ? '' : 's'}
        </span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
          {wf.last_run_status && (
            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', RUN_TONE[wf.last_run_status])}>
              {wf.last_run_status}
            </span>
          )}
          {wf.runs_count > 0 ? `ran ${timeAgo(wf.last_run_at)}` : 'not run yet'}
        </span>
      </div>
    </Link>
  )
}
